import { useApolloClient } from "@apollo/client";
import { getDataOrNull, omit } from "@msys/common";
import {
  LoadingSpinner,
  ModalActions,
  ModalContent,
  ModalDialog,
  ModalTitle,
  ErrorMessage,
} from "@msys/ui";
import { SvgIconComponent } from "@mui/icons-material";
import BusinessIcon from "@mui/icons-material/Business";
import CallIcon from "@mui/icons-material/Call";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EuroIcon from "@mui/icons-material/Euro";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import NotesIcon from "@mui/icons-material/Notes";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { AddressInput, PhoneType } from "../../../../clients/graphqlTypes.js";
import { ErrorBoundary } from "../../../commons/ErrorBoundary.js";
import { CheckboxField } from "../../../commons/form-fields/CheckboxField.js";
import { EmailField } from "../../../commons/form-fields/EmailField.js";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { assertNever } from "../../../utils.js";
import { useCategories } from "../../categories/useCategories.js";
import { AddressField } from "../../addresses/AddressField.js";
import { PhonesField } from "../../phones/PhonesField.js";
import {
  useOnboardingFlowModal_UpdateOrganisationMutation,
  useOnboardingFlowModalQuery,
  type OnboardingFlowModalQuery,
} from "./OnboardingFlowModal.generated.js";
import callAssistantImage from "./OnboardingFlowModal_CallAssistant.png";
import otherImage from "./OnboardingFlowModal_Other.png";
import { OnboardingFlowProjectTypesStage } from "./OnboardingFlowProjectTypesStage.js";
import { OnboardingFlowSpecifyMatrixStage } from "./OnboardingFlowSpecifyMatrixStage.js";
import { OnboardingFlowVerticalsStage } from "./OnboardingFlowVerticalsStage.js";
import { OnboardingFlowWebsitePrefill } from "./OnboardingFlowWebsitePrefill.js";
import { onboardingFlowWizardReducer } from "./onboardingFlowWizardReducer.js";
import {
  initialOnboardingFlowWizardState,
  initialWizardStateFromSuggestions,
  type OnboardingFlowWizardState,
} from "./onboardingFlowWizardState.js";
import {
  normalizeWebsiteUrl,
  websiteUrlPrefillValidationSchema,
  websiteUrlValidationSchema,
} from "./websiteUrl.js";

interface OnboardingFormValues {
  website: string | null;
  fullLegalName: string;
  address: AddressInput | null;
  email: string | null;
  phones: { main: boolean; type: PhoneType; number: string }[] | null;
  managingDirector: string | null;
  confirmation: boolean;
}

export type OnboardingFlowStage =
  | "form"
  | "verticals"
  | "projectTypes"
  | "specifyMatrix"
  | "success";
export const VALID_ONBOARDING_FLOW_STAGES = [
  "form",
  "verticals",
  "projectTypes",
  "specifyMatrix",
  "success",
] satisfies OnboardingFlowStage[];

interface Props {
  stage: OnboardingFlowStage;
  setStage: (newStage: OnboardingFlowStage | null) => void;
}

export type OnboardingViewer = Extract<
  OnboardingFlowModalQuery["viewer"],
  { __typename: "Viewer" }
>;

export const OnboardingFlowModal = ({ stage, setStage }: Props) => {
  const client = useApolloClient();
  const { t } = useTranslate(["Global"]);
  const query = useOnboardingFlowModalQuery({ client });
  const {
    categories,
    loaded: categoriesLoaded,
    loading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useCategories();
  const viewer = getDataOrNull(query.data?.viewer);

  // The wizard starts from the organisation's suggested offered services
  // (design D13), so nothing renders until the fresh viewer response and the
  // taxonomy the mapping needs have loaded. The start state is computed once,
  // so a later refetch neither remounts the content nor drops the user's
  // wizard state. Cached data is not enough: with `cache-and-network` it can
  // be stale while the new response is still loading; the same holds for the
  // categories, so a refresh in flight or failed keeps the wizard closed.
  // Only CRAFTSMAN organisations walk the wizard stages: any other type goes
  // straight from the form to `success`, so it starts with an empty wizard
  // state and neither waits for nor fails on the taxonomy.
  const initialWizardStateRef = React.useRef<OnboardingFlowWizardState | null>(
    null
  );
  const needsTaxonomy = viewer?.organisation.organisationType === "CRAFTSMAN";
  // A failed refresh keeps `query.data` as the cached viewer, so the error must
  // also keep the wizard closed (Retry) rather than lock in stale suggestions.
  if (
    initialWizardStateRef.current === null &&
    viewer &&
    !query.loading &&
    !query.error
  ) {
    if (!needsTaxonomy) {
      initialWizardStateRef.current = initialOnboardingFlowWizardState;
    } else if (categoriesLoaded && !categoriesLoading && !categoriesError) {
      initialWizardStateRef.current = initialWizardStateFromSuggestions(
        viewer.organisation.suggestedOfferedServices,
        categories
      );
    }
  }
  const initialWizardState = initialWizardStateRef.current;

  if (!initialWizardState || !viewer) {
    const failed =
      !!query.error ||
      (!query.loading && !viewer) ||
      (needsTaxonomy &&
        (!!categoriesError || (!categoriesLoading && !categoriesLoaded)));
    return (
      <ModalDialog maxWidth="md">
        {failed ? (
          <ErrorMessage
            message={t("Something went wrong. Please try again.", {
              ns: "Global",
            })}
            button={
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => {
                  void query.refetch();
                  void refetchCategories();
                }}
              >
                {t("Retry", { ns: "Global" })}
              </Button>
            }
          />
        ) : (
          <LoadingSpinner />
        )}
      </ModalDialog>
    );
  }

  return (
    <OnboardingFlowModalContent
      stage={stage}
      setStage={setStage}
      viewer={viewer}
      initialWizardState={initialWizardState}
    />
  );
};

interface ContentProps extends Props {
  viewer: OnboardingViewer;
  initialWizardState: OnboardingFlowWizardState;
}

const OnboardingFlowModalContent = ({
  stage,
  setStage,
  viewer,
  initialWizardState,
}: ContentProps) => {
  // Wizard state lives at the modal root so it survives back/forward
  // navigation between the stage components. State is dropped when the
  // modal unmounts (i.e. when the user closes mid-flow).
  const [wizardState, wizardDispatch] = React.useReducer(
    onboardingFlowWizardReducer,
    initialWizardState
  );
  const navigate = useNavigate();

  const { t } = useTranslate(["Global", "OrganisationProfile"]);

  const client = useApolloClient();
  const hasCallAssistant =
    viewer.organisation.capabilities.includes("callAssistant");

  const [updateOrganisation, { loading: updatingOrganisation }] =
    useOnboardingFlowModal_UpdateOrganisationMutation({ client });

  const [prefilling, setPrefilling] = React.useState(false);

  // CRAFTSMAN orgs walk verticals → projectTypes → optional matrix → success.
  // CLIENT (and any future non-CRAFTSMAN type) skip the new stages entirely
  // and go straight from `form` to `success`. Per-stage `useEffect`
  // auto-skips are defense-in-depth for direct-URL navigation.
  const nextStageAfterForm = (): OnboardingFlowStage =>
    viewer.organisation.organisationType === "CRAFTSMAN"
      ? "verticals"
      : "success";

  // The matrix stage renders a wider 7-column layout; keep the form stage
  // narrow so the data-entry form stays readable.
  const modalMaxWidth =
    stage === "form" ? "sm" : stage === "specifyMatrix" ? "lg" : "md";

  return (
    <ModalDialog maxWidth={modalMaxWidth}>
      <ErrorBoundary>
        {stage === "form" ? (
          <Formik<OnboardingFormValues>
            initialValues={{
              website: viewer.organisation.website ?? null,
              fullLegalName: viewer.organisation.fullLegalName ?? "",
              address: viewer.organisation.billingAddress
                ? omit(viewer.organisation.billingAddress, "__typename", "id")
                : null,
              email: viewer.organisation.email ?? null,
              phones:
                viewer.organisation.phones.length > 0
                  ? viewer.organisation.phones.map(phone =>
                      omit(phone, "__typename", "id")
                    )
                  : [
                      {
                        main: true,
                        type: "WORK",
                        number: "",
                      },
                    ],
              managingDirector: viewer.organisation.managingDirector ?? null,
              confirmation: false,
            }}
            validationSchema={Yup.object().shape({
              // Legacy websites stay passable while unchanged; an edited value
              // must also pass the lookup box's stricter http(s) validation.
              website: websiteUrlValidationSchema().test(
                "edited-website-is-strict",
                "Please enter a valid website URL",
                value =>
                  !value ||
                  value === (viewer.organisation.website ?? null) ||
                  websiteUrlPrefillValidationSchema().isValidSync(value)
              ),
              fullLegalName: Yup.string().required(),
              address: Yup.object().shape({
                streetLines1: Yup.string().required(),
                postalCode: Yup.string().required(),
                city: Yup.string().required(),
                countryCode: Yup.string().required(),
              }),
              email: Yup.string().email().required(),
              phones: Yup.array().of(
                Yup.object().shape({
                  main: Yup.boolean().required(),
                  type: Yup.string().required(),
                  number: Yup.string().required(),
                })
              ),
              managingDirector: Yup.string().nullable(),
              confirmation: Yup.boolean().oneOf(
                [true],
                t(
                  "You need to confirm that the information is correct before proceeding.",
                  { ns: "OrganisationProfile" }
                )
              ),
            })}
            onSubmit={async values => {
              if (!values.confirmation) {
                return;
              }

              const normalizedWebsite = values.website
                ? normalizeWebsiteUrl(values.website)
                : "";

              await updateOrganisation({
                variables: {
                  input: {
                    // "" clears the stored website; `undefined` would leave it unchanged.
                    website: normalizedWebsite,
                    fullLegalName: values.fullLegalName,
                    billingAddress: values.address
                      ? {
                          streetLines1: values.address.streetLines1,
                          postalCode: values.address.postalCode,
                          city: values.address.city,
                          countryCode: values.address.countryCode,
                          location: values.address.location
                            ? {
                                lat: values.address.location.lat,
                                lng: values.address.location.lng,
                              }
                            : undefined,
                        }
                      : undefined,
                    phones: values.phones ?? undefined,
                    email: values.email ?? undefined,
                    managingDirector: values.managingDirector ?? undefined,
                  },
                },
              });
              setStage(nextStageAfterForm());
            }}
          >
            {({ values, setValues, setFieldValue, dirty, isValid }) => (
              <>
                <ModalTitle
                  title={t("Company information", {
                    ns: "OrganisationProfile",
                  })}
                />
                <ModalContent>
                  <Form id="onboarding-form">
                    <Stack spacing={2}>
                      <OnboardingFlowWebsitePrefill
                        organisationName={viewer.organisation.title}
                        initialWebsite={values.website ?? ""}
                        onWebsiteChange={website => {
                          void setFieldValue("website", website || null);
                        }}
                        onLoadingChange={setPrefilling}
                        onDataFound={async ({ website, prefill }) => {
                          await setValues(current => ({
                            ...current,
                            website,
                            fullLegalName:
                              prefill.fullLegalName ?? current.fullLegalName,
                            address: prefill.address
                              ? {
                                  ...omit(
                                    prefill.address,
                                    "__typename",
                                    "location"
                                  ),
                                  location: prefill.address.location
                                    ? omit(
                                        prefill.address.location,
                                        "__typename"
                                      )
                                    : null,
                                }
                              : current.address,
                            email: prefill.email ?? current.email,
                            phones: prefill.phone
                              ? [
                                  {
                                    main: true,
                                    type: "WORK",
                                    number: prefill.phone,
                                  },
                                ]
                              : current.phones,
                            managingDirector:
                              prefill.managingDirector ??
                              current.managingDirector,
                          }));
                        }}
                      />
                      <Divider />
                      <FormControl
                        component="fieldset"
                        variant="standard"
                        disabled={prefilling}
                      >
                        <Stack spacing={1}>
                          <TextField
                            label={t("Full legal name", {
                              ns: "OrganisationProfile",
                            })}
                            name="fullLegalName"
                          />
                          <EmailField
                            label={t("Email", { ns: "OrganisationProfile" })}
                            name="email"
                          />
                          <PhonesField
                            buttonLabel={t("Phone", {
                              ns: "Global",
                            })}
                            name="phones"
                            value={values.phones ?? []}
                          />
                          <AddressField
                            label={t("Address", { ns: "OrganisationProfile" })}
                            name="address"
                          />
                          <TextField
                            label={t("Managing director", {
                              ns: "OrganisationProfile",
                            })}
                            name="managingDirector"
                          />
                          <Box>
                            <CheckboxField
                              label={t(
                                `By clicking "{buttonText}" you are confirming the information`,
                                {
                                  ns: "OrganisationProfile",
                                  buttonText: t("Continue", { ns: "Global" }),
                                }
                              )}
                              name="confirmation"
                            />
                          </Box>
                        </Stack>
                      </FormControl>
                    </Stack>
                  </Form>
                </ModalContent>
                <ModalActions
                  actionButtons={[
                    {
                      label: t("Skip", { ns: "Global" }),
                      handleClick: () => {
                        setStage(nextStageAfterForm());
                      },
                      buttonProps: {
                        color: "primary",
                        variant: "text",
                        endIcon: <KeyboardDoubleArrowRightIcon />,
                        id: "skip-onboarding",
                      },
                    },
                    {
                      label: t("Continue", { ns: "Global" }),
                      buttonProps: {
                        color: "primary",
                        variant: "contained",
                        form: "onboarding-form",
                        type: "submit",
                        endIcon: <ChevronRightIcon />,
                        disabled: prefilling || !dirty || !isValid,
                      },
                    },
                  ]}
                />
              </>
            )}
          </Formik>
        ) : stage === "verticals" ? (
          <OnboardingFlowVerticalsStage
            viewer={viewer}
            state={wizardState}
            dispatch={wizardDispatch}
            setStage={setStage}
          />
        ) : stage === "projectTypes" ? (
          <OnboardingFlowProjectTypesStage
            viewer={viewer}
            state={wizardState}
            dispatch={wizardDispatch}
            setStage={setStage}
          />
        ) : stage === "specifyMatrix" ? (
          <OnboardingFlowSpecifyMatrixStage
            viewer={viewer}
            state={wizardState}
            dispatch={wizardDispatch}
            setStage={setStage}
          />
        ) : stage === "success" ? (
          <>
            <ModalTitle
              title={t("Welcome to Meister Systems!", {
                ns: "OrganisationProfile",
              })}
            />
            {hasCallAssistant ? (
              <SuccessPageLayout
                imageUrl={callAssistantImage}
                textPrimary={t("Hi, I'm AI-Paul!", {
                  ns: "OrganisationProfile",
                })}
                textSecondary={t("Your AI phone assistant.", {
                  ns: "OrganisationProfile",
                })}
                steps={[
                  {
                    IconComponent: CallIcon,
                    title: t("24/7 Availability", {
                      ns: "OrganisationProfile",
                    }),
                    description: t(
                      "Answers all calls on behalf of your business.",
                      {
                        ns: "OrganisationProfile",
                      }
                    ),
                  },
                  {
                    IconComponent: NotesIcon,
                    title: t("Qualify inquiries", {
                      ns: "OrganisationProfile",
                    }),
                    description: t(
                      "Asks all important details about the request.",
                      {
                        ns: "OrganisationProfile",
                      }
                    ),
                  },
                  {
                    IconComponent: EuroIcon,
                    title: t("Prepare quotes", {
                      ns: "OrganisationProfile",
                    }),
                    description: t("Create a quote draft in Meister Systems.", {
                      ns: "OrganisationProfile",
                    }),
                  },
                ]}
                actionButtons={[
                  {
                    label: t("Later", { ns: "Global" }),
                    handleClick: () => {
                      setStage(null);
                    },
                    buttonProps: {
                      color: "primary",
                      variant: "text",
                    },
                  },
                  {
                    label: t("Set up now", { ns: "OrganisationProfile" }),
                    handleClick: () => {
                      navigate("/callassistant/onboarding");
                    },
                    buttonProps: {
                      color: "primary",
                      variant: "contained",
                    },
                  },
                ]}
              />
            ) : (
              <SuccessPageLayout
                imageUrl={otherImage}
                textPrimary={t("You're all set!", {
                  ns: "OrganisationProfile",
                })}
                textSecondary={t(
                  "Pro tip: Complete these settings for optimal performance:",
                  {
                    ns: "OrganisationProfile",
                  }
                )}
                steps={[
                  {
                    IconComponent: BusinessIcon,
                    title: t("Company profile", {
                      ns: "OrganisationProfile",
                    }),
                    description: t(
                      "Complete your company details and add the services you offer.",
                      {
                        ns: "OrganisationProfile",
                      }
                    ),
                  },
                  {
                    IconComponent: PeopleAltIcon,
                    title: t("Invite Team", {
                      ns: "OrganisationProfile",
                    }),
                    description: t(
                      "Send invites to your colleagues to join Meistersystems.",
                      {
                        ns: "OrganisationProfile",
                      }
                    ),
                  },
                  {
                    IconComponent: DescriptionOutlinedIcon,
                    title: t("Customize Documents", {
                      ns: "OrganisationProfile",
                    }),
                    description: t(
                      "Add prices, configure quote settings and personalize your templates.",
                      {
                        ns: "OrganisationProfile",
                      }
                    ),
                  },
                ]}
                actionButtons={[
                  {
                    label: t("Go to settings", { ns: "Global" }),
                    handleClick: () => {
                      navigate("/organisation");
                    },
                    buttonProps: {
                      color: "primary",
                      variant: "text",
                    },
                  },
                  {
                    label: t("Go to dashboard", { ns: "Global" }),
                    handleClick: () => {
                      setStage(null);
                    },
                    buttonProps: {
                      color: "primary",
                      variant: "contained",
                      id: "go-to-dashboard",
                    },
                  },
                ]}
              />
            )}
          </>
        ) : (
          assertNever(stage)
        )}
      </ErrorBoundary>
    </ModalDialog>
  );
};

const SuccessPageLayout = ({
  imageUrl,
  textPrimary,
  textSecondary,
  steps,
  actionButtons,
}: {
  imageUrl: string;
  textPrimary: string;
  textSecondary: string;
  steps: {
    IconComponent: SvgIconComponent;
    title: string;
    description: string;
  }[];
  actionButtons: React.ComponentProps<typeof ModalActions>["actionButtons"];
}) => {
  return (
    <>
      <ModalContent>
        <Stack
          spacing={0}
          sx={{
            marginBottom: 4,
          }}
        >
          <img
            src={imageUrl}
            alt=""
            style={{
              height: "200px",
              objectFit: "cover",
              marginLeft: "-12px",
              marginRight: "-12px",
            }}
          />
          <Stack
            spacing={3}
            sx={{
              paddingX: 1.5,
              paddingY: 3,
            }}
          >
            <Box>
              <Typography variant="h1">{textPrimary}</Typography>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: "bold",
                }}
              >
                {textSecondary}
              </Typography>
            </Box>
            <Grid container columns={{ xs: 1, md: 3 }} spacing={2}>
              {steps.map(({ IconComponent, title, description }, index) => (
                <Grid size={1} key={index}>
                  <Stack spacing={1} direction="row">
                    <IconComponent sx={{ color: "#3867D0" }} />
                    <Stack spacing={1} direction="column">
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: "bold",
                          lineHeight: 1.5,
                        }}
                      >
                        {title}
                      </Typography>
                      <Typography variant="body2">{description}</Typography>
                    </Stack>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Stack>
      </ModalContent>
      <ModalActions actionButtons={actionButtons} />
    </>
  );
};
